import Chart, {ChartTooltipModel} from 'chart.js';
import utils from './utils';
import {
	Area,
	DoughnutChartLabel, DoughnutLabelConfig,
	LabelProvider,
	ResizeStrategy
} from '@/features/reports/components/chartjs/plugins/doughnutLabels/types';

const helpers = Chart.helpers;

export const doughnutlabel: Chart.PluginServiceGlobalRegistration & Chart.PluginServiceRegistrationOptions = {
	afterDatasetsDraw: (chart => setup(chart)),
	afterInit
};

function afterInit(chart: Chart) {
	if (chart.config.type !== 'doughnut') {
		return;
	}

	clearLabels(chart);
}

function setup(chart: Chart) {
	const options = chart.options;
	const ctx = chart.ctx as CanvasRenderingContext2D;
	const pluginData: DoughnutLabelConfig = options.plugins!.doughnutlabel;

	if (chart.config.type !== 'doughnut') {
		return;
	}

	chart.canvas!.addEventListener('mouseleave', () => {
		clearLabels(chart);
	});

	options.tooltips!.custom = (tooltip: ChartTooltipModel) => {
		// Hide if no tooltip
		if (tooltip.opacity === 0) {
			clearLabels(chart);
			return;
		}

		if (tooltip.body) {
			const tipData = tooltip.body.map(item => item.lines)[0][0].split(': ');
			const key = tipData[0];
			const value = Number(tipData[1]);
			const index = tooltip.dataPoints[0].index;
			const labelProvider = pluginData.labelProvider;
			const labels = labelProvider(chart, index, key, value);

			const resolve = helpers.options.resolve;
			const innerLabel: DoughnutChartLabel[] = labels.map(label => ({
				text: label.text,
				font: utils.parseFont(resolve([label.font, {}], ctx, 0)),
				color: resolve([label.color, Chart.defaults.global.defaultFontColor], ctx, 0)
			}));

			pluginData.lastLabels = innerLabel;
			drawLabels(chart, innerLabel);
		}
	};

	if (pluginData.lastLabels) {
		drawLabels(chart, pluginData.lastLabels);
	}
}

function clearLabels(chart: Chart) {
	const options = chart.options;
	const ctx = chart.ctx as CanvasRenderingContext2D;
	const pluginData = options.plugins!.doughnutlabel;
	const resolve = helpers.options.resolve;

	const labelProvider = pluginData.labelProvider as LabelProvider;
	const labels = labelProvider(chart);
	pluginData.lastLabels = labels.map(label => ({
		text: label.text,
		font: utils.parseFont(resolve([label.font, {}], ctx, 0)),
		color: resolve([label.color, Chart.defaults.global.defaultFontColor], ctx, 0)
	}));
}
function drawLabels(chart: Chart, innerLabels: DoughnutChartLabel[]) {
	const ctx = chart.ctx as CanvasRenderingContext2D;
	const textAreaSize = calculateTextAreaSize(ctx, innerLabels, chart);

	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';

	// The center of the inner circle
	const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
	const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);

	// The top Y coordinate of the text area
	const topY = centerY - textAreaSize.height / 2;
	let currentHeight = 0;

	for (const label of innerLabels) {
		ctx.fillStyle = label.color as string;
		ctx.font = label.font.string as string;

		// The Y center of each line
		const lineHeight = (label.font.lineHeight || 24);
		const lineCenterY = topY + lineHeight / 2 + currentHeight;
		currentHeight += lineHeight;

		// Draw each line of text
		// console.log(`label.text: ${label.text}, centerX: ${centerX}, lineCenterY: ${lineCenterY}, color: ${ctx.fillStyle}, font: ${ctx.font}`);
		ctx.fillText(label.text, centerX, lineCenterY);
	}
}

function calculateTextAreaSize(ctx: CanvasRenderingContext2D, innerLabels: DoughnutChartLabel[], chart: Chart) {
	const textAreaSize = utils.textSize(ctx, innerLabels);

	if (isTextAreaTooLarge(textAreaSize, chart)) {
		if (resizeStrategy(chart) === ResizeStrategy.SHRINK) {
			return alterLabelsToFit(ctx, innerLabels, textAreaSize, chart);
		} else {
			return truncateLabelsToFit(ctx, innerLabels, chart);
		}
	}

	return textAreaSize;
}

function alterLabelsToFit(ctx: CanvasRenderingContext2D, innerLabels: DoughnutChartLabel[], textAreaSize: Area, chart: Chart) {
	const resolve = helpers.options.resolve;
	const fitRatio = getAdjustmentRequiredToFitArea(textAreaSize, chart);

	innerLabels.forEach(innerLabel => {
		innerLabel.font.size = Math.floor(Number(innerLabel.font.size) * fitRatio);
		innerLabel.font.lineHeight = undefined;
		innerLabel.font = utils.parseFont(resolve([innerLabel.font, {}], ctx, 0));
	});

	return utils.textSize(ctx, innerLabels);
}

function truncateLabelsToFit(ctx: CanvasRenderingContext2D, innerLabels: DoughnutChartLabel[], chart: Chart) {
	const resolve = helpers.options.resolve;

	for (let i = 0; i < innerLabels.length; i++) {
		const tmpLabels = innerLabels.map(label => Object.assign({}, label));
		tmpLabels.forEach((label, index) => label.text = i === index ? label.text : ' ');
		let textAreaForSingleLabel = utils.textSize(ctx, tmpLabels);
		while (isTextAreaTooLarge(textAreaForSingleLabel, chart)) {
			tmpLabels[i].text = tmpLabels[i].text.substring(0, tmpLabels[i].text.length - 4) + '...';
			tmpLabels[i].font.lineHeight = undefined;
			tmpLabels[i].font = utils.parseFont(resolve([tmpLabels[i].font, {}], ctx, 0));
			textAreaForSingleLabel = utils.textSize(ctx, tmpLabels);
		}
		innerLabels[i].text = tmpLabels[i].text;
		innerLabels[i].font = tmpLabels[i].font;
	}

	return utils.textSize(ctx, innerLabels);
}

function isTextAreaTooLarge(textAreaSize: Area, chart: Chart) {
	return getAdjustmentRequiredToFitArea(textAreaSize, chart) < 1;
}

function getAdjustmentRequiredToFitArea(textAreaSize: Area, chart: Chart) {
	const hypotenuse = Math.sqrt(Math.pow(textAreaSize.width, 2) + Math.pow(textAreaSize.height, 2));
	const innerDiameter = (chart as any).innerRadius * 2;
	return innerDiameter / hypotenuse;
}

function resizeStrategy(chart: Chart): ResizeStrategy {
	const options = chart.options;
	const pluginData = options.plugins!.doughnutlabel;
	return pluginData.resizeStrategy ? pluginData.resizeStrategy : ResizeStrategy.SHRINK;
}
